import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';

import { fetchTopSellersSection } from 'Redux/ducks/algolia-calls';
import BrowseButton from 'Components/Utility/BrowseButton';
import EmailSubscribe from 'Components/EmailSubscribe/EmailSubscribe';
import FluidImg from 'Components/Utility/FluidImg';
import GenreLinks from 'Components/QuickLinks/GenreLinks';
import HitCard from 'Components/Cards/HitCard';
import Meta from 'Components/Utility/SEO/Meta';
import MidPageBanner from 'Components/Banner/MidPageBanner/MidPageBannerContainer';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';

function CyberMonday({ intl, emailSubscribed }) {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [topSellers, setTopSellers] = useState([]);

  const { formatMessage } = intl;
  const messages = defineMessages({
    title: {
      id: 'cybermonday.title',
      defaultMessage: 'Cyber Monday PC Games Deals 2024 - Fanatical',
    },
  });

  useEffect(() => {
    const loadPosts = async () => {
      setHasLoaded(true);
      const fetchedHits = await fetchTopSellersSection(12);
      return setTopSellers(fetchedHits);
    };

    if (!hasLoaded) {
      loadPosts();
    }
  }, [hasLoaded]);

  return (
    <React.Fragment>
      <div className="cyber-monday-page container">
        <Meta title={formatMessage(messages.title)} />
        <h1 className="pt-5 pb-2">
          <FormattedMessage
            id="cybermonday.header"
            defaultMessage="Cyber Monday PC Games Deals"
          />
        </h1>

        <p className="introduction-text">
          <FormattedMessage
            id="cybermonday.intro.one"
            defaultMessage="Missed out on our Cyber Monday deals? Don't worry, Cyber Monday will be back! Why not take a look at our latest deals below or you can get ready for the next sale by adding products to your {wishlist}."
            values={{
              wishlist: <Link to="/wishlist">
                <FormattedMessage
                  id="cybermonday.intro.two.wishlist"
                  defaultMessage="Wishlist"
                />
              </Link>,
            }}
          />
        </p>

        <div className="section-margin">
          <h2>
            <FormattedMessage
              id="cybermonday.when.title"
              defaultMessage="When is Cyber Monday?"
            />
          </h2>
          <p className="introduction-text">
            <FormattedMessage
              id="cybermonday.when.one"
              defaultMessage="Cyber Monday is the first Monday directly after {blackfriday}, which means it will next take place on Monday 2nd December 2024."
              values={{
                blackfriday: <Link to="/black-friday">
                  <FormattedMessage
                    id="cybermonday.when.two.cubermonday"
                    defaultMessage="Black Friday"
                  />
                </Link>,
              }}
            />
          </p>
        </div>

        <div className="email-image-container section-margin">
          <div className="email-seller-container">
            <h2 className="pb-3">
              <FormattedMessage
                id="cybermonday.tips.title"
                defaultMessage="Tips for Cyber Monday"
              />
            </h2>
            <h3 className="h5">
              <FormattedMessage
                id="cybermonday.tip.one.title"
                defaultMessage="Join our Newsletter"
              />
            </h3>
            <p className="introduction-text">
              <FormattedMessage
                id="cybermonday.tip.one"
                defaultMessage="Subscribe to our newsletter and get all the information about our current deals and savings. Closer to the time we'll start updating you about our Cyber Monday sale."
              />
            </p>

            {emailSubscribed &&
              <p className="text-success mb-0">
                <FormattedMessage
                  id="cybermonday.email.subscribed"
                  defaultMessage="You are already subscribed to our newsletter."
                />
              </p>
            }

            {!emailSubscribed &&
              <EmailSubscribe
                singleOptIn
                context="Cyber Monday Page"
              />
            }

            <h3 className="h5 pt-5">
              <FormattedMessage
                id="cybermonday.tip.two.title"
                defaultMessage="Create your Wishlist"
              />
            </h3>
            <p className="introduction-text">
              <FormattedMessage
                id="cybermonday.tip.two"
                defaultMessage="Your Wishlist is a great way to keep track of the games you want. Add products or import your Wishlist from Steam. When your Wishlist items go on sale or into a bundle we will send you an email alert."
              />
            </p>
            <p className="introduction-text">
              <Link to="/wishlist">
                <FormattedMessage
                  id="cybermonday.tip.two.link"
                  defaultMessage="Find out more about Wishlists here."
                />
              </Link>
            </p>
          </div>
          <div className="graphic-container">
            <div className="banner responsive-image-island">
              <div className="responsive-image responsive-image--16by9">
                <FluidImg
                  folder="assets/sales/cyber-monday"
                  src="CyberMondayDeals.jpg"
                  alt="Cyber Monday"
                />
              </div>
            </div>
          </div>
        </div>

        <section className="section-margin">
          <div className="section-heading-container mt-0">
            <h2>
              <FormattedMessage
                id="cybermonday.topsellers.title"
                defaultMessage="Top Sellers"
              />
            </h2>
            <BrowseButton className={'view-all-btn'} location={'top-sellers'} />
          </div>

          {isEmpty(topSellers) &&
            <div className="row grid-row-negative-margin">
              <SkeletonHitCard
                numberOfCards={12}
                xsSize={12}
                smSize={6}
                mdSize={6}
                lgSize={4}
              />
            </div>
          }

          {!isEmpty(topSellers) &&
            <div className="row grid-row-negative-margin">
              {topSellers.map(hit =>
                <HitCard
                  key={hit.objectID}
                  hit={hit}
                />,
              )}
            </div>
          }
        </section>

        <div className="section-margin">
          <MidPageBanner appearsOn="gift_guide" />
        </div>


        <div className="section-margin">
          <GenreLinks />
        </div>
      </div>
    </React.Fragment>
  );
}

const mapStateToProps = ({
  auth: {
    email_newsletter: emailSubscribed,
  },
}) => ({
  emailSubscribed,
});

export default connect(mapStateToProps, null)(injectIntl(CyberMonday));
